import { useTranslation } from "react-i18next";
import { ArrowUpNarrowWide, ArrowDownWideNarrow } from "lucide-react";
import { cn } from "../../utils/cn";
import { Select } from "./Select";

/**
 * Criterio de orden + dirección como UNA unidad visual (select segmentado con
 * el toggle asc/desc pegado a la derecha). Reemplaza los selects con opciones
 * duplicadas por sentido ("Más pedidos"/"Menos pedidos") en los toolbars.
 *
 * `fields`: [{ value, label, defaultDir? }] — defaultDir se aplica al cambiar
 * de criterio porque cada uno tiene un sentido natural (pedidos → desc,
 * nombre → asc); el toggle lo invierte después.
 *
 * field "" = orden de llegada del backend: el toggle queda deshabilitado
 * porque invertir un orden sin criterio no significa nada.
 */
export function SortControl({
  field,
  dir,
  onFieldChange,
  onDirChange,
  fields,
  ariaLabel,
  selectClassName,
  buttonClassName,
}) {
  const { t } = useTranslation();
  const asc = dir === "asc";
  const DirIcon = asc ? ArrowUpNarrowWide : ArrowDownWideNarrow;
  const dirLabel = asc
    ? t("Orden ascendente — cambiar a descendente")
    : t("Orden descendente — cambiar a ascendente");
  return (
    <div className="flex items-stretch">
      <Select
        aria-label={ariaLabel}
        value={field}
        onChange={(e) => {
          const next = e.target.value;
          onFieldChange(next);
          if (next) {
            const def = fields.find((f) => f.value === next)?.defaultDir;
            if (def) onDirChange(def);
          }
        }}
        // focus:z-10 para que el ring no quede recortado por el botón pegado.
        className={cn("relative rounded-r-none focus:z-10", selectClassName)}
        options={[{ value: "", label: t("Orden predeterminado") }, ...fields]}
      />
      <button
        type="button"
        disabled={!field}
        onClick={() => onDirChange(asc ? "desc" : "asc")}
        aria-label={dirLabel}
        title={field ? dirLabel : t("Elige primero un criterio de orden")}
        className={cn(
          "relative -ml-px inline-flex shrink-0 items-center justify-center rounded-lg rounded-l-none border border-border bg-bg text-fg-muted transition-colors",
          "hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-brand-500",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-bg disabled:hover:text-fg-muted",
          buttonClassName,
        )}
      >
        <DirIcon className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
